import { ChangeEvent, FC, WheelEvent } from "react";

import { Box, Collapse, Stack } from "@mui/material";
import { AvailablePaymentCurrency } from "@simplyk/common";

import { splitLabelLastWord } from "./helpers/splitLabelLastWord";

import { useTranslate } from "@/common/hooks/useTranslate";
import { FormAmountField } from "@/forms-design-system/FormAmountField";
import { FormPaymentOrderItem } from "@/forms-design-system/FormPaymentSummary/FormPaymentOrderItem";
import { FormTextButton } from "@/forms-design-system/FormTextButton";
import { FormInfoTooltip } from "@/forms-design-system/FormTooltip";
import { FormTypography } from "@/forms-design-system/FormTypography";

export interface RepeatDonorTipFeeCoverLineProps {
  tipAmount: number;
  tipPercentage: number;
  organizationName: string;
  currency: AvailablePaymentCurrency;
  isEditing: boolean;
  onStartEditing: () => void;
  editedTipCents: number | null;
  onChangeEditedTip: (cents: number | null) => void;
  onBlurTip: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  hasEditedTip: boolean;
  isOrganizationVoice?: boolean;
  formattedAmountWithoutTip?: string;
}

export const RepeatDonorTipFeeCoverLine: FC<RepeatDonorTipFeeCoverLineProps> = ({
  tipAmount,
  tipPercentage,
  organizationName,
  currency,
  isEditing,
  onStartEditing,
  editedTipCents,
  onChangeEditedTip,
  onBlurTip,
  hasEditedTip,
  isOrganizationVoice = false,
  formattedAmountWithoutTip,
}) => {
  const { t } = useTranslate();

  const getLabel = () => {
    if (isOrganizationVoice) {
      return t("common", "paymentSummary.repeatDonorTip.orgVoiceLabel");
    }
    return hasEditedTip
      ? t("common", "paymentSummary.repeatDonorTip.feeCoverLabelEdited", { organizationName })
      : t("common", "paymentSummary.repeatDonorTip.feeCoverLabel", {
          percentage: tipPercentage,
          organizationName,
        });
  };
  const { labelStart, labelLastWord } = splitLabelLastWord(getLabel());

  const tooltipTitle = isOrganizationVoice
    ? t("common", "paymentSummary.repeatDonorTip.orgVoiceTooltip", { amount: formattedAmountWithoutTip })
    : t("common", "paymentSummary.repeatDonorTip.tooltip", { organizationName });

  return (
    <Stack gap={1} data-test="repeat-donor-tip-line">
      <FormPaymentOrderItem
        label={
          <Stack direction="row" alignItems="center" columnGap={1} flexWrap="wrap">
            <FormTypography variant="body2" vibe="moderate" component="span">
              {labelStart}
              <Box component="span" sx={{ whiteSpace: "nowrap" }}>
                {labelLastWord}
                <FormInfoTooltip
                  size="small"
                  sx={{
                    marginInlineStart: 0.5,
                    verticalAlign: "middle",
                    color: (theme) => theme.palette.forms?.text.moderate,
                  }}
                  title={tooltipTitle}
                />
              </Box>
            </FormTypography>
            {!isEditing && (
              <FormTextButton
                onClick={onStartEditing}
                sx={{
                  textDecoration: "underline",
                  color: (theme) => theme.palette.forms?.text.brand,
                  ["&:hover"]: { color: (theme) => theme.palette.forms?.text.brand },
                }}
                data-test="repeat-donor-tip-change-button"
              >
                {t("common", "paymentSummary.repeatDonorTip.changeOrRemove")}
              </FormTextButton>
            )}
          </Stack>
        }
        price={tipAmount}
        currency={currency}
      />
      <Collapse in={isEditing} easing="ease-in" unmountOnExit>
        <Box maxWidth="180px">
          <FormAmountField
            value={editedTipCents}
            label={t("common", "paymentSummary.repeatDonorTip.tipAmount")}
            currency={currency}
            onChange={onChangeEditedTip}
            onBlur={onBlurTip}
            inputProps={{
              inputMode: "decimal",
              onWheel: (event: WheelEvent<HTMLInputElement>) => {
                event.currentTarget.blur();
              },
            }}
            error={typeof editedTipCents === "number" && editedTipCents < 0}
            helperText={
              typeof editedTipCents === "number" && editedTipCents < 0
                ? t("donationForm", "tipsIsGreaterThanZero")
                : undefined
            }
            data-test="repeat-donor-tip-input"
          />
        </Box>
      </Collapse>
    </Stack>
  );
};
